// @flow

import type { CommentOperation } from 'hfty-operator-comments/types'

import type { Crop } from 'fc-model-crops/types'

// Libs
import React, { Component } from 'react'
import _find from 'lodash/find'
import _map from 'lodash/map'
import _size from 'lodash/size'
import { FormattedMessage, injectIntl } from 'react-intl'

// utils
import traverse from 'fc-utils/traverse'
import { getCropUnit, formatToStringifiedNumber } from 'hfty-utils'

// messages
import { messages } from 'hfty-comments'

import { messages as recommendation_messages } from 'hfty-recommendations'

// Components
import Button from '@react-mdc/button'
import Textfield from '@react-mdc/textfield'
import FormField from '@react-mdc/form-field'
import Select from 'react-select'
import { Errors } from 'hfty-components-errors'

// constants
import { PROP_CROP_ID, PROP_NAME as PROP_CROP_NAME, COTTON } from 'fc-model-crops'

import { PROP_YIELD_TARGET, PROP_CROP } from 'hfty-model-comments'

import {
  PROP_REMOVAL_CROP_GROWN,
  PROP_REMOVAL_YIELD,
  PROP_REMOVAL_CROP,
  PROP_RECOMMENDATION_TYPE,
} from 'hfty-model-recommendations'

// Styles
import { grey, primary, white, error } from 'hfty-styles/src/colors'

const styles = {
  container: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: 300,
    backgroundColor: '#fff',
    color: '#fff',
    boxShadow: '-2px 0 2px rgba(0, 0, 0, 0.2)',
    padding: 15,
    overflow: 'auto',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    margin: '30px 0 10px 0',
  },
  formFieldContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    color: grey(),
    fontSize: 14,
  },
  unit: {
    marginLeft: 5,
  },
  header: {
    padding: '20px 0px',
    width: '100%',
    backgroundColor: primary(),
    color: white(),
    textAlign: 'center',
    fontWeight: 500,
    marginBottom: 20,
  },
  yieldStyle: ({ hasValue, hasTriedSubmition }: { hasValue: boolean, hasTriedSubmition: boolean }): Object => ({
    color: !hasValue && hasTriedSubmition && error(),
  }),
  title: {
    marginBottom: 10,
  },
}

// types
type Props = {
  crops: Array<Crop>,
  intl: Object,
  operation: CommentOperation,
  onChange: Function,
  onCancel: Function,
  onSubmit: Function,
  currentUserUnits: string,
  recommendationTypeOptions: Array<Object>,
}

class AddComment extends Component {
  props: Props
  onSubmit: Function
  onCancel: Function

  constructor(props: Props) {
    super(props)
    this.state = {
      hasTriedSubmition: false,
    }
    this.onSubmit = this.onSubmit.bind(this)
    this.onCancel = this.onCancel.bind(this)
  }

  onChange(value: any, modelProp: string) {
    const intVal = value && !isNaN(value) ? parseInt(value) : ''
    const { crops, operation } = this.props
    const currentCropId = modelProp === PROP_CROP ? value : traverse(['model', PROP_CROP])(operation)
    const cottonCrop = _find(crops, ({ name = '' }: Object): boolean => name.toLowerCase() === COTTON)
    const isSupportDecimalYield = currentCropId === cottonCrop.id
    let updatedModel

    switch (modelProp) {
      case PROP_REMOVAL_CROP_GROWN:
        const cropId = traverse(['model', PROP_CROP], { default: null })(this.props.operation)
        updatedModel = intVal
          ? {
              [modelProp]: intVal,
              [PROP_REMOVAL_CROP]: cropId,
            }
          : {
              [modelProp]: intVal,
              [PROP_REMOVAL_YIELD]: null,
              [PROP_REMOVAL_CROP]: null,
            }
        break
      case PROP_YIELD_TARGET:
        updatedModel = {
          [modelProp]: formatToStringifiedNumber({ value, isDecimal: isSupportDecimalYield }),
        }
        break
      default:
        const yieldTarget = traverse(['model', PROP_YIELD_TARGET])(operation)

        updatedModel = {
          [modelProp]: value,
          [PROP_YIELD_TARGET]: formatToStringifiedNumber({ value: yieldTarget, isDecimal: isSupportDecimalYield }),
        }
        break
    }

    this.props.onChange({
      ...this.props.operation,
      model: updatedModel,
    })
  }

  onSubmit(e: Event) {
    e.preventDefault()
    if (this.validate()) {
      this.props.onSubmit([this.props.operation])
      this.setState({ hasTriedSubmition: false })
    } else {
      this.setState({ hasTriedSubmition: true })
    }
  }

  validate(): boolean {
    const { operation } = this.props
    const targetYield = traverse(['model', PROP_YIELD_TARGET])(operation)
    const removalYield = traverse(['model', PROP_REMOVAL_YIELD])(operation)
    const removalCropGrown = traverse(['model', PROP_REMOVAL_CROP_GROWN])(operation)
    return (
      targetYield != null && targetYield !== '' && (!removalCropGrown || (removalYield != null && removalYield !== ''))
    )
  }

  onCancel(e: Event) {
    e.preventDefault()
    this.setState({ hasTriedSubmition: false })
    this.props.onCancel([this.props.operation])
  }

  renderCropSelector(): React.Element<any> {
    const { operation, crops } = this.props

    const cropOptions: Object = _map(crops, (crop: Crop): Object => ({
      value: crop[PROP_CROP_ID],
      label: crop[PROP_CROP_NAME],
    }))

    return (
      <div style={styles.formFieldContainer}>
        <FormField>
          <label htmlFor="crop-selector">
            <FormattedMessage {...messages['crop']} />*
          </label>
          <Select
            name="crop-selector"
            clearable={false}
            value={traverse(['model', PROP_CROP])(operation)}
            options={cropOptions}
            onChange={(e: Event) => {
              this.onChange(traverse(['value'])(e), PROP_CROP)
            }}
          />
        </FormField>
      </div>
    )
  }
  renderYieldTargetInput(): React.Element<any> {
    const { operation, currentUserUnits, crops } = this.props
    const targetYield = traverse(['model', PROP_YIELD_TARGET], { default: '' })(operation)

    const crop = _find(crops, (crop: Crop): boolean => crop[PROP_CROP_ID] === traverse(['model', PROP_CROP])(operation))

    const cropUnit = getCropUnit(crop, currentUserUnits)
    return (
      <div style={styles.formFieldContainer}>
        <FormField>
          <Textfield disabled={operation.submitting}>
            <Textfield.Input
              id={`label-${operation.operationId}`}
              value={targetYield}
              onChange={(e: Event) => {
                this.onChange(traverse(['target', 'value'])(e), PROP_YIELD_TARGET)
              }}
            />
            <Textfield.Label
              htmlFor="usage-1"
              style={styles.yieldStyle({
                hasValue: targetYield != null && targetYield !== '',
                hasTriedSubmition: this.state.hasTriedSubmition,
              })}
            >
              <FormattedMessage {...messages['targetYield']} />
              <span style={styles.unit}>
                (<FormattedMessage {...messages[cropUnit]} />)
              </span>
              *
            </Textfield.Label>
          </Textfield>
        </FormField>
      </div>
    )
  }

  renderRemovalCropGrown(): React.Element<any> {
    const { operation } = this.props
    const removalCropGrown = traverse(['model', PROP_REMOVAL_CROP_GROWN])(operation)
    const cropGrownOptions = [
      {
        value: true,
        label: <FormattedMessage {...recommendation_messages['AddRecommendation.yes']} />,
      },
      {
        value: false,
        label: <FormattedMessage {...recommendation_messages['AddRecommendation.no']} />,
      },
    ]

    return (
      <div style={{ ...styles.formFieldContainer, marginTop: 20 }}>
        <div style={styles.title}>
          <FormattedMessage {...recommendation_messages['AddRecommendation.cropGrownQuestion']} />
        </div>
        <FormField>
          <Select
            clearable={false}
            name="removal-crop-grown-selector"
            value={removalCropGrown}
            options={cropGrownOptions}
            onChange={(e: Event) => {
              this.onChange(traverse(['value'])(e), PROP_REMOVAL_CROP_GROWN)
            }}
          />
        </FormField>
      </div>
    )
  }

  renderRemovalCrop(): React.Element<any> {
    const { crops, operation, intl } = this.props
    const removalCropGrown = traverse(['model', PROP_REMOVAL_CROP_GROWN])(operation)
    const removalCropOptions = _map(crops, (crop: Crop): Object => ({
      value: crop[PROP_CROP_ID],
      label: crop[PROP_CROP_NAME],
    }))

    return (
      removalCropGrown && (
        <div style={{ ...styles.formFieldContainer, marginTop: 20 }}>
          <FormField
            title={intl.formatMessage(recommendation_messages['AddRecommendation.removalCropTooltip'])}
            disabled={operation.submitting}
          >
            <label htmlFor="removal-crop-selector">
              <FormattedMessage {...recommendation_messages['AddRecommendation.removalCrop']} />
            </label>
            <Select
              name="removal-crop-selector"
              clearable={false}
              value={traverse(['model', PROP_REMOVAL_CROP])(operation)}
              options={removalCropOptions}
              onChange={(e: Event) => {
                this.onChange(traverse(['value'])(e), PROP_REMOVAL_CROP)
              }}
            />
          </FormField>
        </div>
      )
    )
  }

  renderRemovalYield(): React.Element<any> {
    const { operation, crops, intl, currentUserUnits } = this.props
    const removalCropGrown = traverse(['model', PROP_REMOVAL_CROP_GROWN])(operation)
    const removalYield = traverse(['model', PROP_REMOVAL_YIELD])(operation)

    const removalCrop = _find(
      crops,
      (crop: Crop): boolean => crop[PROP_CROP_ID] === traverse(['model', PROP_REMOVAL_CROP])(operation)
    )

    const cropUnit = getCropUnit(removalCrop, currentUserUnits)

    return (
      removalCropGrown && (
        <div style={styles.formFieldContainer}>
          <FormField>
            <Textfield
              title={intl.formatMessage(recommendation_messages['AddRecommendation.removalYieldTooltip'])}
              disabled={operation.submitting}
            >
              <Textfield.Input
                id={`label-${operation.operationId}`}
                value={removalYield}
                onChange={(e: Event) => {
                  this.onChange(traverse(['target', 'value'])(e), PROP_REMOVAL_YIELD)
                }}
              />
              <Textfield.Label
                htmlFor="usage-1"
                style={styles.yieldStyle({
                  hasValue: removalYield != null && removalYield !== '',
                  hasTriedSubmition: this.state.hasTriedSubmition,
                })}
              >
                <FormattedMessage {...recommendation_messages['AddRecommendation.removalYield']} />
                <span style={styles.unit}>
                  (<FormattedMessage {...messages[cropUnit]} />)
                </span>
                *
              </Textfield.Label>
            </Textfield>
          </FormField>
        </div>
      )
    )
  }

  renderRecommendationType(): React.Element<any> {
    const { operation, recommendationTypeOptions } = this.props

    return (
      <div style={{ ...styles.formFieldContainer, marginTop: 15 }}>
        <div style={styles.title}>
          <FormattedMessage {...recommendation_messages['AddRecommendation.recommendationType']} />
        </div>
        <FormField>
          <Select
            clearable={false}
            name="recommendation-type-selector"
            value={traverse(['model', PROP_RECOMMENDATION_TYPE])(operation)}
            options={recommendationTypeOptions}
            onChange={(e: Event) => {
              this.onChange(traverse(['value'])(e), PROP_RECOMMENDATION_TYPE)
            }}
          />
        </FormField>
      </div>
    )
  }

  render(): ?React.Element<any> {
    const { operation } = this.props
    const isAdding = traverse(['operation'])(operation) === 'ADD'

    if (_size(this.props.operation) && isAdding) {
      const hasErrors = operation && operation.errors
      return (
        <form style={styles.container} onSubmit={this.onSubmit}>
          <div style={styles.header}>
            <FormattedMessage {...messages['addComment']} />
          </div>
          {this.renderCropSelector()}
          {this.renderYieldTargetInput()}
          {this.renderRecommendationType()}
          {/* {this.renderRemovalCropGrown()} */}
          {/* {this.renderRemovalCrop()} */}
          {/* {this.renderRemovalYield()} */}
          <FormField>{hasErrors && <Errors errors={operation.errors} />}</FormField>
          <div style={styles.buttonsContainer}>
            <Button raised primary onClick={this.onSubmit}>
              {'Confirm'}
            </Button>
            <Button raised primary onClick={this.onCancel}>
              {'Cancel'}
            </Button>
          </div>
        </form>
      )
    }

    return null
  }
}

export default injectIntl(AddComment)
